import React, { useState, useEffect, forwardRef } from 'react';
import { Typography, ButtonGroup, Button } from '@material-ui/core';
import useStyles from './styles';
import { get, set, size, flatten } from 'global/lib/lodash';
import useRetrieveData from 'global/hooks/useRetrieveData';
import clsx from 'clsx';
import { HeatChart } from 'global/components/HeatChart';
import { useSelector } from 'react-redux';
import { buildFilters } from 'global/utils/filter';
import { pluralize } from 'global/utils';
import { cleanCopy } from 'global/utils';
import moment from 'moment';

// ##filters
// assessment_id,
// scorecard_id,
// superset_id,
// checklist_id,
// checkpoint_id,
// realm_id*,
// realm_type**,
// depth,
// start_date*,
// end_date*

const TransformData = (raw) => {
  const { data, meta } = raw;
  const rels = get(meta, 'relationships', {});
  const hasError = get(meta, 'error');

  if (hasError) {
    return meta;
  }

  const findRel = (sel, id) => {
    return get(rels, sel, []).find((f) => f.id == id);
  };

  const isDateRow = (d) => {
    return ['year', 'month', 'quarter'].indexOf(d) > -1;
  };

  return data.map((d) => {
    const ids = Object.keys(d).filter((d) => {
      const isId = d.indexOf('_id') > -1;
      return isDateRow(d) || isId;
    });
    const relationships = {};
    ids.map((idkey) => {
      const [key] = idkey.split('_');
      if (isDateRow(idkey)) {
        const { month, year } = d;
        const dateKey = idkey;
        let _month = parseInt(month) - 1; //months start at 0 in moment
        _month = isNaN(_month) ? 1 : _month;
        const tme = dateKey === 'quarter' ? moment().month(_month).year(year).startOf('month').toISOString() : moment().month(_month).year(year).startOf(dateKey).toISOString();
        set(relationships, [dateKey, 'id'], tme);
      } else {
        set(relationships, key, findRel(pluralize(key), get(d, idkey)));
      }
    });
    return {
      ...d,
      relationships,
    };
  });
};

export default forwardRef((props, refs) => {
  const classes = useStyles();
  const filter = useSelector((state) => get(state, 'filter.reports.data'));
  const realm = useSelector((state) => get(state, 'auth.realm'));
  const [chips, setChips] = useState({});

  const [state, setState] = useState({
    selected: {
      rows: null,
      columns: null,
    },
  });

  const realmDisableCheck = (_state, compareKey = 'rows') => {
    const rowSelected = get(_state, `selected.${compareKey}`);
    const disabled = ['builder', 'division', 'community', 'lot'];

    if (disabled.indexOf(rowSelected) > -1) {
      return true;
    }
    return false;
  };

  const getColKey = (p) => {
    return get(p, 'selectedRow.tableBy') || 'superset';
  };

  const [content] = useState({
    KEYS: ['rows', 'columns'],
    rows: {
      title: 'Rows',
      buttonGroups: [
        [
          { children: 'Scorecard', id: 'scorecard', tier: 'scorecard', showColAvg: true, onRowSelect: () => setState((s) => ({ ...s, selected: { ...s.selected, rows: 'superset' } })) },
          {
            children: 'System',
            id: 'superset',
            parent: 'scorecard_header',
            tableBy: 'edition',
            tier: 'scorecard',
            showColAvg: true,
            onRowSelect: () => setState((s) => ({ ...s, selected: { ...s.selected, rows: 'checklist' } })),
          },
          {
            children: 'Checklist',
            id: 'checklist',
            parent: 'superset',
            tableBy: 'superset',
            tier: 'superset',
            showColAvg: true,
            onRowSelect: () => setState((s) => ({ ...s, selected: { ...s.selected, rows: 'checkpoint' } })),
          },
          { children: 'Checkpoint', id: 'checkpoint', parent: 'checklist', tableBy: 'checklist', showColAvg: true, isDisabled: () => true },
        ],
        [
          { children: 'Builder', id: 'builder', tableBy: 'edition', groupBy: 'realm', showColAvg: true, isDisabled: (s) => realmDisableCheck(s, 'columns'), isVisible: () => !!size(props.vendors) },
          { children: 'Division', id: 'division', tableBy: 'edition', groupBy: 'realm', showColAvg: true, isDisabled: (s) => realmDisableCheck(s, 'columns') },
          { children: 'Community', id: 'community', tableBy: 'edition', groupBy: 'realm', showColAvg: true, isDisabled: (s) => realmDisableCheck(s, 'columns') },
          { children: 'Lot', id: 'lot', groupBy: 'realm', showColAvg: true, tableBy: 'edition', isDisabled: (s) => realmDisableCheck(s, 'columns') },
        ],
      ],
    },
    columns: {
      title: 'Columns',
      buttonGroups: [
        [
          { children: 'Builder', id: 'builder', groupBy: 'realm', colKey: getColKey, isDisabled: (s) => realmDisableCheck(s, 'rows'), isVisible: () => !!size(props.vendors) },
          { children: 'Division', id: 'division', groupBy: 'realm', colKey: getColKey, isDisabled: (s) => realmDisableCheck(s, 'rows') },
          { children: 'Community', id: 'community', groupBy: 'realm', colKey: getColKey, isDisabled: (s) => realmDisableCheck(s, 'rows') },
          { children: 'Lot', id: 'lot', groupBy: 'realm', colKey: getColKey, isDisabled: (s) => realmDisableCheck(s, 'rows') },
        ],
        [
          { children: 'Yearly', id: 'yearly', colKey: getColKey, groupBy: 'relationships.year.id' },
          { children: 'Quarterly', id: 'quarterly', colKey: getColKey, groupBy: 'relationships.quarter.id' },
          { children: 'Monthly', id: 'monthly', colKey: getColKey, groupBy: 'relationships.month.id' },
        ],
        [
          { children: 'Assessment', id: 'assessments', groupBy: 'assessment', colKey: getColKey, isDisabled: (s) => realmDisableCheck(s, 'rows') },
          { children: 'Company', id: 'company', groupBy: 'vendor', colKey: getColKey, isVisible: () => !!size(props.vendors) },
          { children: 'Author', id: 'author', groupBy: 'author', colKey: getColKey },
        ],
      ],
    },
  });

  let [opts, setOpts] = useState({
    build: ['dataReport'],
    waitFor: null,
    useEffect: true,
    useNamespace: false,
    returnRaw: true,
    params: {},
    transformData: TransformData,
  });

  let [opts2, setOpts2] = useState(cleanCopy(opts));

  const { data, loading } = useRetrieveData({ url: '/data-reports' }, opts);
  const headerRow = useRetrieveData({ url: '/data-reports' }, opts2);

  const canLoad = !!(get(state, 'selected.rows') && get(state, 'selected.columns'));

  const getChipFilters = () => {
    const obj = {};
    Object.keys(chips).map((k) => {
      set(obj, k, get(chips, [k, 'id']));
    });
    return obj;
  };

  const getSelectedRow = (sel = 'rows') => {
    return flatten(get(content, `${sel}.buttonGroups`, [])).find((r) => r.id === get(state, `selected.${sel}`));
  };

  useEffect(() => {
    props.onFilters && props.onFilters(chips);
  }, [chips]);

  useEffect(() => {
    if (canLoad) {
      const _opts = { ...opts, waitFor: undefined };

      const filters = buildFilters(filter, {
        dontFilter: ['ownership', 'quality_type', 'superset_id', 'user_id'],
        convertName: {
          realm: 'realm_id',
        },
        dontSort: true,
        includeRealm: true,
      });

      if (get(filters, 'filter.scorecard_id') === 0) {
        set(filters, 'filter.scorecard_id', undefined);
      }

      const chipfilts = getChipFilters();

      set(_opts, 'params.filter', {
        ...chipfilts,
        ...filters.filter,
        scorecard_id: get(chipfilts, 'scorecard_id', get(filters, 'filter.scorecard_id')),
        //if the scorecard is set, the edition_id filter is redundant, and sometimes problematic
        edition_id: get(filters, 'filter.scorecard_id', get(chipfilts, 'scorecard_id')) ? undefined : get(chipfilts, 'edition_id'),
      });
      set(_opts, 'params.meta', state.selected);

      setOpts({ ..._opts });

      const _opts2 = cleanCopy(_opts);
      const opt2row = get(getSelectedRow(), 'parent', '');
      set(_opts2, 'params.meta.rows', opt2row);
      set(_opts2, 'transformData', TransformData);
      setOpts2(_opts2);
    }
  }, [state, filter, realm, chips]);

  const onClick = (key, btn) => {
    const sel = `selected.${key}`;
    const isSelected = get(state, sel) === btn.id;
    set(state, sel, isSelected ? null : btn.id);
    setState({ ...state });
  };

  const onRowSelect = (filt) => {
    setChips((c) => ({ ...c, ...filt }));
    get(getSelectedRow(), 'onRowSelect', () => 0)();
  };

  const removeFilter = (k) => {
    setChips({
      ...chips,
      [k]: undefined,
    });
  };

  set(refs, 'current.removeFilter', removeFilter);

  return (
    <div className={classes.container}>
      <div className={classes.filters}>
        {content.KEYS.map((k) => {
          return (
            <div key={k} className={classes.filterContainer}>
              <Typography variant="subtitle1" className={classes.overline}>
                {get(content, `${k}.title`)}
              </Typography>

              {get(content, `${k}.buttonGroups`, []).map((btnGroup, i) => {
                return (
                  <ButtonGroup key={i} className={classes.buttonGroup} orientation="vertical" variant="contained">
                    {btnGroup.map((btn, _i) => {
                      const disabled = get(btn, 'isDisabled', () => false)(state);
                      const visible = get(btn, 'isVisible', () => true)();
                      const active = get(state, `selected.${k}`) === btn.id;
                      btn = {
                        ...btn,
                        disabled: active ? false : disabled,
                      };
                      return !visible ? null : <Button key={_i} {...btn} className={clsx(active && classes.buttonActive)} onClick={() => onClick(k, btn)} />;
                    })}
                  </ButtonGroup>
                );
              })}
            </div>
          );
        })}
      </div>

      <HeatChart
        {...state.selected}
        selectedRow={getSelectedRow()}
        selectedColumn={getSelectedRow('columns')}
        data={data}
        chips={chips}
        loading={canLoad ? loading || headerRow.loading : false}
        headerRow={headerRow.data}
        onRowSelect={onRowSelect}
      />
    </div>
  );
});
